import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api, { getApiErrorMessage } from '../../api';
import { notifyCountsChanged } from '../../utils/counts';
import AppShell from '../AppShell';
import ThemePicker from '../ThemePicker';
import ShareSettings from '../presentations/ShareSettings';
import { QaLocked } from './QaSessions';
import { copyToClipboard } from './qaUtils';
import { qaParticipantUrl, useQaUpdates } from './useQa';
import './Qa.css';

const TABS = [
    { id: 'moderation', label: 'На модерации', statuses: ['checking', 'pending'] },
    { id: 'live', label: 'Вопросы', statuses: ['approved'] },
    { id: 'answered', label: 'Отвеченные', statuses: ['answered'] },
    { id: 'hidden', label: 'Скрытые и отклонённые', statuses: ['hidden', 'rejected'] },
];

const STATUS_LABELS = {
    checking: 'Проверяется ИИ',
    pending: 'Ждёт одобрения',
    approved: 'Опубликован',
    answered: 'Отвечен',
    hidden: 'Скрыт',
    rejected: 'Отклонён',
};

function Toggle({ id, checked, onChange, disabled, children, hint }) {
    return (
        <label className="qa-toggle" htmlFor={id}>
            <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
            <span className="qa-toggle__switch" aria-hidden />
            <span className="qa-toggle__text">
                {children}
                {hint && <span className="qa-toggle__hint">{hint}</span>}
            </span>
        </label>
    );
}

function QaHost() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [locked, setLocked] = useState(null);
    const [tab, setTab] = useState('live');
    const [busyId, setBusyId] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [copied, setCopied] = useState(null);
    const [editingTitle, setEditingTitle] = useState(null);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/qa-sessions/${sessionId}`);
            setData(res.data);
            setError(null);
        } catch (err) {
            if (err.response?.status === 403) setLocked(getApiErrorMessage(err, ''));
            else setError(err.response?.status === 404 ? 'Сессия вопросов не найдена' : getApiErrorMessage(err, 'Не удалось загрузить вопросы'));
        }
    }, [sessionId]);

    useEffect(() => { load(); }, [load]);
    useQaUpdates(data?.public_id, load);

    useEffect(() => {
        if (data?.title) document.title = `Вопросы из зала — ${data.title}`;
    }, [data?.title]);

    const updateSession = async (patch) => {
        setSavingSettings(true);
        try {
            const res = await api.patch(`/qa-sessions/${sessionId}`, patch);
            setData((prev) => ({ ...prev, title: res.data.title, settings: res.data.settings }));
            if ('enabled' in patch) notifyCountsChanged();
            return true;
        } catch (err) {
            alert(getApiErrorMessage(err, 'Не удалось сохранить'));
            load();
            return false;
        } finally {
            setSavingSettings(false);
        }
    };

    const updateSettings = (patch) => {
        setData((prev) => ({ ...prev, settings: { ...prev.settings, ...patch } }));
        updateSession(patch);
    };

    const saveTitle = async (e) => {
        e.preventDefault();
        if (await updateSession({ title: editingTitle })) setEditingTitle(null);
    };

    const act = async (question, patch) => {
        setBusyId(question.id);
        try {
            await api.patch(`/qa-sessions/${sessionId}/questions/${question.id}`, patch);
            await load();
        } catch (err) {
            alert(getApiErrorMessage(err, 'Не удалось выполнить действие'));
        } finally {
            setBusyId(null);
        }
    };

    const clearAll = async () => {
        if (!window.confirm('Удалить все вопросы этой сессии? Ссылка и QR-код продолжат работать. Действие нельзя отменить.')) return;
        try {
            await api.delete(`/qa-sessions/${sessionId}/questions`);
            await load();
        } catch (err) {
            alert(getApiErrorMessage(err, 'Не удалось удалить вопросы'));
        }
    };

    const removeSession = async () => {
        if (!window.confirm(`Удалить сессию «${data.title}» вместе с вопросами? Ссылка и QR-код перестанут работать.`)) return;
        try {
            await api.delete(`/qa-sessions/${sessionId}`);
            notifyCountsChanged();
            navigate('/qa');
        } catch (err) {
            alert(getApiErrorMessage(err, 'Не удалось удалить сессию'));
        }
    };

    const exportExcel = async () => {
        setExporting(true);
        try {
            const res = await api.get(`/qa-sessions/${sessionId}/export`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            // Латиница: часть браузеров теряет имя файла с русскими буквами и сохраняет его без расширения
            a.download = `questions_${data.public_id}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            alert(getApiErrorMessage(err, 'Не удалось скачать вопросы'));
        } finally {
            setExporting(false);
        }
    };

    const copy = async (what, text) => {
        if (await copyToClipboard(text)) {
            setCopied(what);
            setTimeout(() => setCopied(null), 2000);
        } else {
            window.prompt('Скопируйте:', text);
        }
    };

    let content;
    if (locked !== null) {
        content = <QaLocked message={locked} />;
    } else if (error) {
        content = (
            <div className="qa-sessions">
                <div className="qa-card qa-card--center">
                    <h2>{error}</h2>
                    <div className="qa-card__buttons">
                        <button type="button" className="btn btn--secondary" onClick={() => navigate('/qa')}>Все сессии</button>
                    </div>
                </div>
            </div>
        );
    } else if (!data) {
        content = <div className="qa-loading">Загрузка…</div>;
    } else {
        const { settings, questions } = data;
        const link = qaParticipantUrl(data.public_id);
        const screenLink = `/qa/${data.public_id}/screen`;
        const embedCode = `<iframe src="${window.location.origin}${screenLink}" width="100%" height="600" style="border:0" allow="fullscreen"></iframe>`;
        const byTab = (t) => questions.filter((q) => t.statuses.includes(q.status));
        const currentTab = TABS.find((t) => t.id === tab) || TABS[1];
        const items = byTab(currentTab);
        const pinned = questions.find((q) => q.pinned);

        content = (
            <div className="qa-host">
                <div className="qa-host__head">
                    <button type="button" className="qa-back" onClick={() => navigate('/qa')}>← Все сессии</button>
                    {editingTitle === null ? (
                        <div className="qa-host__title-row">
                            <h1 className="qa-host__title">{data.title}</h1>
                            <button type="button" className="qa-link-btn" onClick={() => setEditingTitle(data.title)}>Переименовать</button>
                        </div>
                    ) : (
                        <form className="qa-host__title-form" onSubmit={saveTitle}>
                            <input value={editingTitle} onChange={(e) => setEditingTitle(e.target.value)} maxLength={120} autoFocus aria-label="Название сессии" />
                            <button type="submit" className="btn btn--primary btn--sm" disabled={savingSettings || !editingTitle.trim()}>Сохранить</button>
                            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditingTitle(null)}>Отмена</button>
                        </form>
                    )}
                    <div className="qa-host__actions">
                        <a className="btn btn--primary" href={screenLink} target="_blank" rel="noreferrer"><svg className="qa-icon" viewBox="0 0 24 24" aria-hidden><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></svg>Экран для проектора</a>
                        <button type="button" className="btn btn--secondary" onClick={exportExcel} disabled={exporting || questions.length === 0}>
                            {exporting ? 'Готовим файл…' : 'Скачать в Excel'}
                        </button>
                    </div>
                </div>

                <div className="qa-host-layout">
                    <aside className="qa-host-side">
                        <section className="qa-card">
                            <h2 className="qa-card__title">Настройки</h2>
                            <Toggle
                                id="qa-enabled"
                                checked={settings.enabled}
                                disabled={savingSettings}
                                onChange={(v) => updateSettings({ enabled: v })}
                                hint={settings.enabled ? 'Участники могут задавать вопросы' : 'Новые вопросы не принимаются'}
                            >
                                Приём вопросов
                            </Toggle>
                            <Toggle
                                id="qa-premod"
                                checked={settings.premoderation}
                                disabled={savingSettings}
                                onChange={(v) => updateSettings({ premoderation: v })}
                                hint="Вопрос виден всем только после вашего одобрения"
                            >
                                Премодерация
                            </Toggle>
                            <Toggle
                                id="qa-ai"
                                checked={settings.ai_moderation}
                                disabled={savingSettings}
                                onChange={(v) => updateSettings({ ai_moderation: v })}
                                hint="Оскорбления и спам отклоняются автоматически"
                            >
                                Автомодерация
                            </Toggle>
                            <div className="qa-theme">
                                <ThemePicker
                                    id="qa-host-theme"
                                    label="Оформление для участников и экрана"
                                    value={settings.theme || 'light'}
                                    disabled={savingSettings}
                                    onChange={(v) => updateSettings({ theme: v })}
                                />
                            </div>
                            <ShareSettings
                                kind="qa" id={data.id} value={data.share} paid
                                onChange={(share) => setData((prev) => ({ ...prev, share }))}
                            />
                        </section>

                        <section className="qa-card qa-share">
                            <h2 className="qa-card__title">Ссылка для участников</h2>
                            <div className="qa-share__qr"><QRCodeSVG value={link} size={168} level="M" /></div>
                            <div className="qa-share__row">
                                <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Ссылка для участников" />
                                <button type="button" className="btn btn--secondary btn--sm" onClick={() => copy('link', link)}>
                                    {copied === 'link' ? 'Скопировано' : 'Копировать'}
                                </button>
                            </div>
                            {!settings.enabled && <p className="qa-hint">Включите «Приём вопросов», чтобы участники могли писать.</p>}
                        </section>

                        <details className="qa-card qa-embed">
                            <summary>Встроить экран на сайт или в вебинар</summary>
                            <p className="qa-hint">Вставьте код на страницу сайта, в курс LMS или вебинарную комнату — там появится экран с вопросами, как на проекторе.</p>
                            <textarea readOnly value={embedCode} rows={3} onFocus={(e) => e.target.select()} aria-label="Код для встраивания" />
                            <button type="button" className="btn btn--secondary btn--sm" onClick={() => copy('embed', embedCode)}>
                                {copied === 'embed' ? 'Скопировано' : 'Копировать код'}
                            </button>
                        </details>

                        <div className="qa-danger-zone">
                            {questions.length > 0 && (
                                <button type="button" className="btn btn--danger-outline btn--sm" onClick={clearAll}>Удалить все вопросы</button>
                            )}
                            <button type="button" className="btn btn--danger-outline btn--sm" onClick={removeSession}>Удалить сессию</button>
                        </div>
                    </aside>

                    <main className="qa-host-main">
                        {pinned && (
                            <div className="qa-card qa-onscreen">
                                <div className="qa-badge qa-badge--live">● На экране</div>
                                <p className="qa-onscreen__text">{pinned.text}</p>
                                <div className="qa-onscreen__actions">
                                    <button type="button" className="btn btn--success btn--sm" disabled={busyId === pinned.id} onClick={() => act(pinned, { status: 'answered' })}>✓ Отвечен</button>
                                    <button type="button" className="btn btn--secondary btn--sm" disabled={busyId === pinned.id} onClick={() => act(pinned, { pinned: false })}>Убрать с экрана</button>
                                </div>
                            </div>
                        )}

                        <div className="qa-tabs" role="tablist">
                            {TABS.map((t) => {
                                const count = byTab(t).length;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={tab === t.id}
                                        className={`qa-tab${tab === t.id ? ' qa-tab--active' : ''}${t.id === 'moderation' && count > 0 ? ' qa-tab--alert' : ''}`}
                                        onClick={() => setTab(t.id)}
                                    >
                                        {t.label} <span className="qa-tab__count">{count}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {items.length === 0 ? (
                            <p className="qa-empty">
                                {tab === 'live' ? 'Опубликованных вопросов пока нет.' : 'Здесь пусто.'}
                            </p>
                        ) : (
                            <ul className="qa-list">
                                {items.map((q) => (
                                    <li key={q.id} className={`qa-item qa-item--host${q.pinned ? ' qa-item--pinned' : ''}`}>
                                        <div className="qa-item__body">
                                            <p className="qa-item__text">{q.text}</p>
                                            <div className="qa-item__meta">
                                                <span className={`qa-badge qa-badge--${q.status}`}>{STATUS_LABELS[q.status]}</span>
                                                <span className="qa-item__votes">▲ {q.votes}</span>
                                                <span>{new Date(q.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
                                                {q.moderation_reason && <span className="qa-item__reason">Причина: {q.moderation_reason}</span>}
                                            </div>
                                            <div className="qa-item__actions">
                                                {(q.status === 'pending' || q.status === 'checking') && (
                                                    <>
                                                        <button type="button" className="btn btn--success btn--sm" disabled={busyId === q.id} onClick={() => act(q, { status: 'approved' })}>Опубликовать</button>
                                                        <button type="button" className="btn btn--danger-outline btn--sm" disabled={busyId === q.id} onClick={() => act(q, { status: 'rejected' })}>Отклонить</button>
                                                    </>
                                                )}
                                                {q.status === 'approved' && (
                                                    <>
                                                        <button type="button" className="btn btn--primary btn--sm" disabled={busyId === q.id} onClick={() => act(q, { pinned: !q.pinned })}>
                                                            {q.pinned ? 'Убрать с экрана' : 'На экран'}
                                                        </button>
                                                        <button type="button" className="btn btn--success btn--sm" disabled={busyId === q.id} onClick={() => act(q, { status: 'answered' })}>✓ Отвечен</button>
                                                        <button type="button" className="btn btn--secondary btn--sm" disabled={busyId === q.id} onClick={() => act(q, { status: 'hidden' })}>Скрыть</button>
                                                    </>
                                                )}
                                                {['answered', 'hidden', 'rejected'].includes(q.status) && (
                                                    <button type="button" className="btn btn--secondary btn--sm" disabled={busyId === q.id} onClick={() => act(q, { status: 'approved' })}>
                                                        Вернуть в вопросы
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </main>
                </div>
            </div>
        );
    }

    return <AppShell section="qa" title={data?.title || 'Вопросы из зала'}>{content}</AppShell>;
}

export default QaHost;
